import EditProductProvider from '@/contexts/EditProductContext';
import OfferProvider from '@/contexts/OfferContext';
import { CheckoutProvider } from '@/contexts/CheckoutContext';
import { Navigate, Outlet } from 'react-router-dom';
import CaktoAnalyticsGuard from '@/auth/CaktoAnalyticsGuard';
import {
  AddOffer,
  CaktoAnalyticsPage,
  Offers,
  OrderBumpsPageV2,
  ProductAffiliatesPage,
  AffiliateProductSettingsPage,
  ProductCheckoutLinksPage,
  ProductCheckoutPageV2,
  ProductCoProductionsPage,
  ProductCouponsPageV2,
  ProductEditInfoPage,
  ProductEditPaymentModelPage,
  ProductEditSettingsPage,
  ProductExitPopupPage,
  ProductPixelsPage,
  ProductUpsellResumePage,
  ProductWhatsappPage,
  ResumeMembersAreaPage,
  ResumeMembersExternalPage,
  ResumeMembersV2Page,
  ResumeMembersV3Page,
} from './elements';
import { withoutOffersAddPage } from './productResumeRouteUtils';

/**
 * Children compartilhados das subpáginas do `products/resume-shell/:id`.
 *
 * @param {{ indexElement?: import('react').ReactNode | null }} options
 */
export function createProductResumeChildren({ indexElement = null } = {}) {
  return [
    {
      index: true,
      element: indexElement,
    },
    {
      path: 'upsell',
      element: (
        <EditProductProvider>
          <ProductUpsellResumePage />
        </EditProductProvider>
      ),
    },
    {
      // O guard de quem pode ver é o do shell (`isResumeShellSegmentAllowed`), como nas outras
      // seções: sem recurso de WhatsApp liberado a URL cai fora antes de a página montar.
      path: 'whatsapp',
      element: (
        <EditProductProvider>
          <ProductWhatsappPage />
        </EditProductProvider>
      ),
    },
    {
      // Guard próprio além do filtro do shell: a tela não depende do EditProductProvider e pode
      // ser montada fora do shell no futuro sem perder a checagem de liberação.
      path: 'analytics',
      element: (
        <CaktoAnalyticsGuard>
          <CaktoAnalyticsPage />
        </CaktoAnalyticsGuard>
      ),
    },
    {
      path: 'members-area',
      element: (
        <EditProductProvider>
          <ResumeMembersAreaPage />
        </EditProductProvider>
      ),
    },
    {
      path: 'membersv2',
      element: (
        <EditProductProvider>
          <ResumeMembersV2Page />
        </EditProductProvider>
      ),
    },
    {
      path: 'membersv3',
      element: (
        <EditProductProvider>
          <ResumeMembersV3Page />
        </EditProductProvider>
      ),
    },
    {
      path: 'members-external',
      element: (
        <EditProductProvider>
          <ResumeMembersExternalPage />
        </EditProductProvider>
      ),
    },
    {
      path: 'checkout-links',
      element: (
        <EditProductProvider>
          <ProductCheckoutLinksPage />
        </EditProductProvider>
      ),
    },
    {
      path: 'customize-checkout',
      element: (
        <EditProductProvider>
          <CheckoutProvider>
            <ProductCheckoutPageV2 />
          </CheckoutProvider>
        </EditProductProvider>
      ),
    },
    {
      path: 'offers',
      element: (
        <OfferProvider>
          <Outlet />
        </OfferProvider>
      ),
      children: [
        {
          index: true,
          element: <Offers />,
        },
        {
          path: 'add',
          element: <AddOffer />,
        },
      ],
    },
    {
      path: 'coupons',
      element: <ProductCouponsPageV2 />,
    },
    {
      path: 'order-bumps',
      element: <OrderBumpsPageV2 />,
    },
    {
      path: 'exit-popup',
      element: (
        <EditProductProvider>
          <ProductExitPopupPage />
        </EditProductProvider>
      ),
    },
    {
      path: 'pixels',
      element: (
        <EditProductProvider>
          <ProductPixelsPage />
        </EditProductProvider>
      ),
    },
    {
      path: 'coproduction',
      element: <ProductCoProductionsPage />,
    },
    {
      path: 'affiliates',
      element: (
        <EditProductProvider>
          <ProductAffiliatesPage />
        </EditProductProvider>
      ),
    },
  ];
}

const shellEditRoutes = [
  {
    path: 'product-info',
    element: (
      <EditProductProvider>
        <ProductEditInfoPage />
      </EditProductProvider>
    ),
  },
  {
    path: 'payment-model',
    element: (
      <EditProductProvider>
        <ProductEditPaymentModelPage />
      </EditProductProvider>
    ),
  },
  {
    path: 'settings',
    element: (
      <EditProductProvider>
        <ProductEditSettingsPage />
      </EditProductProvider>
    ),
  },
];

/**
 * Subpáginas do afiliado em `products/resume-shell/:id/affiliates/:short_id`.
 */
export function createProductResumeAffiliateChildren() {
  return [
    {
      path: 'affiliates/:short_id',
      children: [
        {
          index: true,
          element: <Navigate to="checkout-links" replace />,
        },
        {
          path: 'checkout-links',
          element: (
            <EditProductProvider>
              <ProductCheckoutLinksPage />
            </EditProductProvider>
          ),
        },
        {
          path: 'coupons',
          element: (
            <EditProductProvider>
              <ProductCouponsPageV2 />
            </EditProductProvider>
          ),
        },
        {
          path: 'settings',
          element: (
            <EditProductProvider>
              <AffiliateProductSettingsPage />
            </EditProductProvider>
          ),
        },
      ],
    },
  ];
}

/**
 * Children exclusivos do shell `products/resume-shell/:id`.
 */
export function createProductResumeShellChildren() {
  return [
    {
      index: true,
      element: <Navigate to="payment-model" replace />,
    },
    ...shellEditRoutes,
    ...createProductResumeAffiliateChildren(),
    ...withoutOffersAddPage(
      createProductResumeChildren({ indexElement: null }).filter((route) => !route.index)
    ),
  ];
}
